import React, { useDeferredValue, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Body, H1, Label } from '../ui/Typography';
import Button from '../ui/Button';
import { findPortfolioProject, portfolioProjects } from '../../data/projects';

export default function ProjectsPage() {
  const [query, setQuery] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(portfolioProjects[0]?.id ?? null);
  const deferredQuery = useDeferredValue(query);

  const projects = useMemo(() => {
    const search = deferredQuery.trim().toLowerCase();

    if (!search) {
      return portfolioProjects;
    }

    return portfolioProjects.filter((project) => {
      return [
        project.name,
        project.client,
        project.medium,
        project.description,
        project.tags.join(' '),
      ]
        .join(' ')
        .toLowerCase()
        .includes(search);
    });
  }, [deferredQuery]);

  const selectedProject = selectedProjectId
    ? projects.find((project) => project.id === selectedProjectId) ?? projects[0] ?? findPortfolioProject(selectedProjectId)
    : projects[0] ?? null;

  return (
    <div className="portfolio-shell space-y-12 pb-24">
      <section className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
        <div className="space-y-4">
          <Label className="text-neonOrange">Projects</Label>
          <H1 className="max-w-4xl text-[clamp(3rem,8vw,6rem)] leading-[0.92]">A portfolio built around systems that can move.</H1>
        </div>
        <Body className="max-w-2xl text-lg text-text-secondary">
          A selection of hospitality launches, campaign art, and image systems designed for social, environmental graphics, and rollout pressure.
        </Body>
      </section>

      <section className="grid gap-8 lg:grid-cols-[0.78fr_1.22fr]">
        <aside className="space-y-4 border border-border-subtle bg-surface-elevated/70 p-5 backdrop-blur-sm">
          <div className="space-y-2">
            <Label>Filter the work</Label>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search project, client, or medium"
              className="w-full border border-border-subtle bg-surface-input px-4 py-3 text-sm text-text-primary placeholder:text-text-muted"
              aria-label="Search projects"
            />
          </div>

          <div className="grid gap-3">
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => setSelectedProjectId(project.id)}
                className={`border p-4 text-left transition-colors ${
                  selectedProject?.id === project.id
                    ? 'border-neonOrange bg-neonOrange/10'
                    : 'border-border-subtle bg-black/20 hover:border-offWhite/50'
                }`}
              >
                <p className="text-xs uppercase tracking-[0.18em] text-text-muted">{project.client}</p>
                <p className="mt-2 text-lg font-display uppercase tracking-tight text-offWhite">{project.name}</p>
                <p className="mt-2 text-sm text-text-secondary">{project.medium}</p>
              </button>
            ))}
          </div>
        </aside>

        <div className="space-y-6">
          {selectedProject ? (
            <article className="border border-border-subtle bg-surface-card/80 p-5 backdrop-blur-sm">
              <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
                <div className="space-y-4">
                  <div className="aspect-[16/11] overflow-hidden bg-charcoal">
                    <img
                      src={selectedProject.images[0]}
                      alt={`${selectedProject.name} featured work`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  {selectedProject.images[1] ? (
                    <div className="aspect-[16/9] overflow-hidden border border-border-subtle bg-charcoal">
                      <img
                        src={selectedProject.images[1]}
                        alt={`${selectedProject.name} secondary detail`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : null}
                </div>

                <div className="space-y-4">
                  <Label className="text-gold">{selectedProject.client}</Label>
                  <h2 className="text-4xl font-display uppercase tracking-tight">{selectedProject.name}</h2>
                  <p className="text-sm uppercase tracking-[0.18em] text-text-muted">
                    {selectedProject.year} / {selectedProject.medium}
                  </p>
                  <Body className="text-text-secondary">{selectedProject.description}</Body>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags.map((tag) => (
                      <span
                        key={tag}
                        className="border border-border-subtle px-3 py-1 text-xs uppercase tracking-[0.18em] text-text-secondary"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button className="px-5 py-3 text-sm font-mono uppercase tracking-[0.18em]">
                      Ask about a project like this
                    </Button>
                  </Link>
                </div>
              </div>
            </article>
          ) : (
            <div className="border border-dashed border-border-subtle p-8 text-text-secondary">
              No projects matched that search.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
